import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '~/providers/AppProvider';
import { getBoatsByCoordinates } from '~/api/services/boats';
import SEO from '~/components/seo';
import styled from 'styled-components';
import Layout from '~/components/layout';
import BoatsFilter from '~/components/filter/boatsFilter';
import BoatsList from '~/components/boats/boatsList';
import BoatMap from '~/components/search/boatMap';
import { useRouter } from 'next/router';
import { getQueryParamsForSearch } from '~/utils/urlParser';
import isEqual from 'lodash.isequal';

import NoBoatsFound from '~/components/boats/noBoatsFound';
import { parseBoatsForSearchMap, getEmptyBoats } from '~/utils/objects';
import { InView } from 'react-intersection-observer';
import { MobileMapToggle } from '@boatsetter-backup/js-component-lib';
import useWindowSize from '~/hooks/useWindowSize';
import { getCenterFromRectangle, getPlaceFromCoords } from '~/utils/map';

import { t } from '@lingui/macro';
import { withI18n } from '@lingui/react';

interface Props {
  i18n: any;
}

const SearchPage = ({ i18n }: Props) => {
  const appCtx = useContext(AppContext);
  const router = useRouter();
  const [map, setMap] = useState();

  const [boatsToShow, setBoatsToShow] = useState([]);
  const [boatsTotalNumber, setBoatsTotalNumber] = useState(0);

  const [hasBoats, setHasBoats] = useState(true);
  const [parsedBoatsForMap, setParsedBoatsForMap] = useState([]);

  const [hoveredResultId, setHoveredResultId] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [forceShowMapByButton, setForceShowMapByButton] = useState(false);

  const [footerInView, setFooterInView] = useState(false);
  const [mapFullScreen, setMapFullScreen] = useState(false);
  const [searchPlaceName, setSearchPlaceName] = useState('');

  const [lastNumberOfBoats, setLasNumberOfBoats] = useState(10);

  const size = useWindowSize();

  const getPlaceName = () => {
    const mapCentre = getCenterFromRectangle({
      sw_lat: parseFloat(String(router.query.latitudeSw)),
      ne_lat: parseFloat(String(router.query.latitudeNe)),
      sw_lng: parseFloat(String(router.query.longitudeSw)),
      ne_lng: parseFloat(String(router.query.longitudeNe)),
    });

    getPlaceFromCoords(mapCentre.lat, mapCentre.lon, setSearchPlaceName, router.query.near);
  };

  const [cachedBB, setCachedBB] = useState<any>({});
  const [cachedQuery, setCachedQuery] = useState<any>({});

  const cleanQueryParams = (object: any) => {
    const copy = { ...object };
    delete copy.longitudeNe;
    delete copy.longitudeSw;
    delete copy.latitudeSw;
    delete copy.latitudeNe;

    return copy;
  };

  const fetchBoats = async () => {
    let boats = null;
    let boatsTotalNumber = 0;

    const queryParamsForSearch = getQueryParamsForSearch(router.query, appCtx);

    if (router.query.latitudeSw && router.query.longitudeSw && router.query.latitudeNe && router.query.longitudeNe) {
      const {
        // @ts-expect-error
        longitudeNe,
        // @ts-expect-error
        longitudeSw,
        // @ts-expect-error
        latitudeSw,
        // @ts-expect-error
        latitudeNe,
      } = await getBoatsByCoordinates(queryParamsForSearch)
        .then((response) => {
          boats = response.data.Data;
          boatsTotalNumber = response.data.TotalRecordsCount;
          return {
            boats,
            boatsTotalNumber,
            latitudeSw: response.data.LatitudeSw,
            latitudeNe: response.data.LatitudeNe,
            longitudeSw: response.data.LongitudeSw,
            longitudeNe: response.data.LongitudeNe,
          };
        })
        .catch((e) => {
          console.error(e);
        });

      setCachedBB({
        latitudeNe,
        latitudeSw,
        longitudeSw,
        longitudeNe,
      });
      setCachedQuery(cleanQueryParams(router.query));

      if (queryParamsForSearch.automaticallyExpandSearch) {
        router.push({
          pathname: router.pathname,
          query: {
            ...router.query,
            latitudeNe,
            latitudeSw,
            longitudeSw,
            longitudeNe,
          },
        });

        // @ts-expect-error
        map?.fitBounds(
          new window.google.maps.LatLngBounds(
            new window.google.maps.LatLng(parseFloat(String(latitudeSw)), parseFloat(String(longitudeSw))),
            new window.google.maps.LatLng(parseFloat(String(latitudeNe)), parseFloat(String(longitudeNe)))
          ),
          0
        );
      }
    }
    return { boats, boatsTotalNumber };
  };

  useEffect(() => {
    if (searchPlaceName === '' || localStorage?.getItem('newSearch') == 'true') {
      getPlaceName();
      localStorage?.removeItem('newSearch');
    }

    // @ts-expect-error
    const latitudeNe = +parseFloat(router.query.latitudeNe).toFixed(10);
    // @ts-expect-error
    const latitudeSw = +parseFloat(router.query.latitudeSw).toFixed(10);
    // @ts-expect-error
    const longitudeSw = +parseFloat(router.query.longitudeSw).toFixed(10);
    // @ts-expect-error
    const longitudeNe = +parseFloat(router.query.longitudeNe).toFixed(10);

    // console.log(latitudeNe, cachedBB?.latitudeNe)
    // console.log(latitudeSw, cachedBB?.latitudeSw)
    // console.log(longitudeSw, cachedBB?.longitudeSw)
    // console.log(longitudeNe, cachedBB?.longitudeNe)
    // console.log(router.query.action)

    if (
      latitudeNe === cachedBB?.latitudeNe &&
      latitudeSw === cachedBB?.latitudeSw &&
      longitudeSw === cachedBB?.longitudeSw &&
      longitudeNe === cachedBB?.longitudeNe &&
      isEqual(cachedQuery, cleanQueryParams(router.query))
    ) {
      // console.log('wont fetch')
      return;
    }

    (async () => {
      appCtx.actions.setLoading(true);
      setLasNumberOfBoats(boatsToShow.length !== 0 ? boatsToShow.length : 10);
      let fetchedBoats = null;
      fetchedBoats = await fetchBoats();
      setBoatsToShow(fetchedBoats.boats);
      setBoatsTotalNumber(fetchedBoats.boatsTotalNumber);

      setHasBoats(!!(fetchedBoats.boats !== null && fetchedBoats.boats.length !== 0));

      if (fetchedBoats.boats !== null && fetchedBoats.boats.length !== 0) {
        setParsedBoatsForMap(parseBoatsForSearchMap(fetchedBoats.boats));
      } else {
        setParsedBoatsForMap([]);
      }

      appCtx.actions.setLoading(false);
    })();

    if (router.asPath) {
      window?.localStorage.setItem('searchUrl', JSON.stringify(router.asPath));
    }
  }, [JSON.stringify(router.query)]);

  useEffect(() => {
    if (size.width < 960) {
      setShowMap(false);
      setMapFullScreen(true);
    }
  }, [size]);

  const togleShowMap = () => {
    if (showMap) {
      setShowMap(false);
      setForceShowMapByButton(false);
    } else {
      setShowMap(true);
      setForceShowMapByButton(true);
    }
  };

  return (
    <Layout>
      <SEO
        title={`${i18n._(t`seo.search.title`)}`}
        description={`${i18n._(t`seo.search.description`)}`}
        noIndex={true}
      />

      <BoatsFilter
        setShowMap={setShowMap}
        showMap={showMap}
        mapFullScreen={mapFullScreen}
        setForceShowMapByButton={setForceShowMapByButton}
        forceShowMapByButton={forceShowMapByButton}
        togleShowMap={togleShowMap}
      />
      <SearchWrapper>
        <Wrapper
          className={`${showMap && forceShowMapByButton ? 'map_shown' : ''} ${
            mapFullScreen && showMap ? 'map_fullscreen' : ''
          }`}
        >
          {boatsToShow === null ? (
            <NoBoatsFound />
          ) : (
            <>
              {!hasBoats && !appCtx.state.loading && <NoBoatsFound />}
              {hasBoats && appCtx.state.loading && (
                <BoatsList
                  placeName={searchPlaceName}
                  hoveredResultId={'xxx'}
                  setHoveredResultId={() => {}}
                  isMapShownFullScreen={mapFullScreen && forceShowMapByButton}
                  isMapShown={showMap && forceShowMapByButton}
                  boats={getEmptyBoats(lastNumberOfBoats)}
                  boatsTotalNumber={boatsTotalNumber}
                />
              )}
              {hasBoats && !appCtx.state.loading && (
                <BoatsList
                  placeName={searchPlaceName}
                  hoveredResultId={hoveredResultId}
                  setHoveredResultId={setHoveredResultId}
                  isMapShownFullScreen={mapFullScreen && forceShowMapByButton}
                  isMapShown={showMap && forceShowMapByButton}
                  boats={boatsToShow}
                  boatsTotalNumber={boatsTotalNumber}
                />
              )}
              {!hasBoats && appCtx.state.loading && (
                <BoatsList
                  placeName={searchPlaceName}
                  hoveredResultId={'xxx'}
                  setHoveredResultId={() => {}}
                  isMapShownFullScreen={mapFullScreen}
                  isMapShown={showMap}
                  boats={getEmptyBoats(lastNumberOfBoats)}
                  boatsTotalNumber={boatsTotalNumber}
                />
              )}
            </>
          )}
        </Wrapper>
        {showMap && forceShowMapByButton && (
          <MapWrapper visible={showMap && forceShowMapByButton}>
            <BoatMap
              map={map}
              setMap={setMap}
              mapFullScreen={mapFullScreen}
              parsedBoats={parsedBoatsForMap}
              hoveredResultId={hoveredResultId}
              setHoveredResultId={setHoveredResultId}
              footerInView={footerInView}
              showMap={showMap}
            />
          </MapWrapper>
        )}
      </SearchWrapper>
      <InView
        as="div"
        className="hidden"
        style={{ height: '35px' }}
        rootMargin="15px 0px 0px 0px"
        onChange={(i, entry) => {
          setFooterInView(i);
        }}
      >
        <div />
      </InView>
      <MobileMapToggleWrapper>
        <MobileMapToggle
          listViewTrans="List View"
          mapViewTrans="Map View"
          mapVisible={showMap && forceShowMapByButton}
          onClick={togleShowMap}
        />
      </MobileMapToggleWrapper>
    </Layout>
  );
};

const MapWrapper = styled.div`
  display: ${({ visible }) => (visible ? 'block' : 'none')};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 6em;
  width: 100%;

  @media (max-width: 640px) {
    padding: 1em;
    padding-top: 5em;
  }

  &.map_shown {
    width: 60%;
    /* max-width: 750px; */
  }

  &.map_fullscreen {
    display: none;
  }
`;

const SearchWrapper = styled.div`
  display: flex;
  /* min-height: 94vh; */
  /* position: relative; */

  min-height: calc(100vh - 70px);
`;

const MobileMapToggleWrapper = styled.div`
  @media (min-width: 960px) {
    display: none;
  }
`;

export default withI18n()(SearchPage);
